<template>
  <div v-loading="isLoading">
    <el-table
      ref="rateCardTable"
      :data="rateCards"
      stripe
      @selection-change="handleSelectionChange"
    >
      <el-table-column type="expand">
        <template slot-scope="props">
          <RateCardRatesTable :rate-card="props.row" />
        </template>
      </el-table-column>

      <el-table-column
        v-if="canUpdate || canDelete"
        type="selection"
      />

      <el-table-column
        label="Code"
        prop="code"
        min-width="120px"
        show-overflow-tooltip
        :formatter="(row, col, cellValue) => cellValue || '-'"
      >
        <template slot-scope="scope">
          <span>{{ scope.row.code || '-' }}</span>
          <i
            v-if="scope.row.notes && scope.row.notes.length > 0"
            class="el-icon-potato-strips ml-2"
            title="This rate card has notes"
            @click.native="notes(scope.$index, scope.row)"
          />
        </template>
      </el-table-column>

      <el-table-column
        label="Name"
        prop="name"
        sortable
        min-width="320px"
        show-overflow-tooltip
        :formatter="(row, col, cellValue) => cellValue || '-'"
      />

      <el-table-column
        label="Type"
        width="120px"
      >
        <template slot-scope="scope">
          <el-tag
            v-if="scope.row.type === 'buy'"
            type="info"
            size="small"
          >
            Buy
          </el-tag>

          <el-tag
            v-else-if="scope.row.type === 'sell'"
            type="primary"
            size="small"
          >
            Sell
          </el-tag>

          <el-tag
            v-else-if="scope.row.type === 'global'"
            type="success"
            size="small"
          >
            Global
          </el-tag>

          <el-tag
            v-else
            type="warning"
            size="small"
          >
            Unknown
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column
        label="Started At"
        prop="date_start"
        sortable
        width="120px"
        :formatter="(row, col, cellValue) => cellValue ? $moment(cellValue).format('DD/MM/YYYY') : '-'"
      />

      <el-table-column
        label="Ended At"
        prop="date_end"
        width="120px"
        :formatter="(row, col, cellValue) => cellValue ? $moment(cellValue).format('DD/MM/YYYY') : '-'"
      />

      <el-table-column
        label="Carrier"
        prop="carrier"
        width="180px"
        sortable
        :sort-method="sortCarrier"
        show-overflow-tooltip
        :formatter="(row, col, cellValue) => cellValue ? (cellValue.name || '-') : '-'"
      />

      <el-table-column
        label="Updated By"
        prop="profile"
        width="120px"
        show-overflow-tooltip
        :formatter="(row, col, cellValue) => cellValue ? (cellValue.name_display ? cellValue.name_display : (cellValue.name_full || '-')) : '-'"
      />

      <el-table-column
        label="Status"
        align="center"
        width="120px"
      >
        <template slot-scope="scope">
          <el-tag
            v-if="scope.row.active"
            type="success"
            size="small"
          >
            Active
          </el-tag>

          <el-tag
            v-else
            type="warning"
            size="small"
          >
            Inactive
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column
        label="Actions"
        align="right"
        width="270px"
      >
        <template #header>
          <el-button
            v-if="canUpdate"
            size="mini"
            type="primary"
            :disabled="$_.isEmpty(multiSelected)"
            @click.native="isShowBulkUpdate = true"
          >
            Bulk Update
          </el-button>

          <el-button
            v-if="canDelete"
            size="mini"
            type="danger"
            :disabled="$_.isEmpty(multiSelected)"
            @click.native="bulkDelete"
          >
            Bulk Delete
          </el-button>
        </template>

        <template slot-scope="scope">
          <el-button
            v-if="canUpdate"
            size="mini"
            @click.native="editRateCard(scope.$index, scope.row)"
          >
            Edit
          </el-button>

          <el-button
            v-if="canDelete"
            size="mini"
            type="danger"
            @click.native="deleteRateCard(scope.$index, scope.row)"
          >
            Delete
          </el-button>

          <el-dropdown
            :hide-on-click="false"
            style="margin-left: 10px;"
            trigger="click"
          >
            <el-button size="mini">
              <FontAwesomeIcon :icon="['far', 'ellipsis-h']" />
            </el-button>

            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item
                v-if="canUpdate && scope.row.type !== 'global'"
                @click.native="linkedAccounts(scope.$index, scope.row)"
              >
                Accounts
              </el-dropdown-item>

              <el-dropdown-item
                v-if="canCreate"
                @click.native="duplicateRateCard(scope.$index, scope.row)"
              >
                Duplicate
              </el-dropdown-item>

              <el-dropdown-item @click.native="history(scope.$index, scope.row)">
                History
              </el-dropdown-item>

              <el-dropdown-item @click.native="exportCSV(scope.$index, scope.row)">
                Export CSV
              </el-dropdown-item>

              <el-dropdown-item
                :disabled="scope.row.type === 0"
                @click.native="exportPDF(scope.$index, scope.row)"
              >
                Export PDF
              </el-dropdown-item>
              <el-dropdown-item @click.native="notes(scope.$index, scope.row)">
                Notes
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </template>
      </el-table-column>
    </el-table>

    <!-- Pagination -->
    <el-pagination
      class="text-center pt-3"
      :current-page.sync="page"
      :page-sizes="[10, 20, 50, 100]"
      :page-size.sync="perPage"
      layout="total, sizes, prev, pager, next, jumper"
      :total="total"
      @size-change="loadTable"
      @current-change="loadTable"
    />

    <!-- Import -->
    <ImportDialog
      v-if="isShowImportDialog"
      :is-show.sync="isShowImportDialog"
      @imported="loadTable"
    />

    <!-- Edit Form -->
    <RateCardEditForm
      v-if="isShowEditForm"
      ref="editForm"
      :rate-card.sync="rateCard"
      :is-show.sync="isShowEditForm"
      @saved="loadTable"
    />

    <!-- Linked Accounts Form -->
    <LinkedAccountsForm
      v-if="isShowLinkedAccountsForm"
      ref="linkedAccountsForm"
      :is-show.sync="isShowLinkedAccountsForm"
      :rate-card="rateCard"
    />

    <!-- Duplicate Form -->
    <RateCardDuplicateForm
      v-if="isShowDuplicateForm"
      ref="duplicateForm"
      :is-show.sync="isShowDuplicateForm"
      :old-rate-card="rateCard"
    />

    <!-- Bulk Update -->
    <RateCardBulkUpdate
      v-if="isShowBulkUpdate"
      ref="bulkUpdateForm"
      :rate-cards="multiSelected"
      :is-show.sync="isShowBulkUpdate"
    />

    <!-- Export CSV Form -->
    <RateCardExportCSV
      v-if="isShowExportCSV"
      ref="exportCSVForm"
      :rate-card="exportCSVRateCard"
      :is-show.sync="isShowExportCSV"
    />

    <!-- Export PDF Form -->
    <RateCardExportPDF
      v-if="isShowExportPDF"
      ref="exportPDFForm"
      :rate-card="exportPDFRateCard"
      :is-show.sync="isShowExportPDF"
    />
  </div>
</template>

<script>
import Vue from 'vue'
import ImportDialog from '@/components/Application/Manage/RateCards/Import'
import RateCardEditForm from '@/components/Application/Manage/RateCards/RateCardEditForm'
import LinkedAccountsForm from '@/components/Application/Manage/RateCards/LinkedAccountsForm'
import RateCardDuplicateForm from '@/components/Application/Manage/RateCards/RateCardDuplicateForm'
import RateCardRatesTable from '@/components/Application/Manage/RateCards/RateCardRatesTable'
import RateCardBulkUpdate from '@/components/Application/Manage/RateCards/RateCardBulkUpdate'
import RateCardExportCSV from '@/components/Application/Manage/RateCards/RateCardExportCSV'
import RateCardExportPDF from '@/components/Application/Manage/RateCards/RateCardExportPDF'
import RateCardNotesDialog from '@/components/Application/Manage/RateCards/RateCardNotesDialog'
import Filter, { IS } from '@/plugins/sdk/Filter'
import { CREATE, UPDATE, DELETE } from '@/plugins/user'

export default {
  components: {
    ImportDialog,
    RateCardEditForm,
    LinkedAccountsForm,
    RateCardDuplicateForm,
    RateCardRatesTable,
    RateCardBulkUpdate,
    RateCardExportCSV,
    RateCardExportPDF
  },
  props: {
    filters: {
      type: Array,
      required: true
    },
    sort: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      actions: [
        [
          {
            hidden: this.$user.cannot(CREATE, this.$sdk.ratecard),
            name: 'Import',
            icon: ['fal', 'upload'],
            event: () => {
              this.isShowImportDialog = true
            }
          }
        ],
        [
          {
            hidden: this.$user.cannot(CREATE, this.$sdk.ratecard),
            name: 'Create',
            icon: ['fal', 'plus'],
            event: () => {
              this.rateCard = new this.$sdk.ratecard()
              this.isShowEditForm = true
            }
          },
          {
            name: 'Refresh',
            icon: ['fal', 'sync'],
            event: this.loadTable
          }
        ]
      ],
      isLoading: true,
      isShowImportDialog: false,
      isShowEditForm: false,
      isShowLinkedAccountsForm: false,
      isShowDuplicateForm: false,
      isShowBulkUpdate: false,
      isShowExportCSV: false,
      isShowExportPDF: false,
      page: 1,
      perPage: 10,
      total: 0,
      rateCards: [],
      rateCard: null,
      exportCSVRateCard: {},
      exportPDFRateCard: {},
      multiSelected: [],
      notesDialogInstance: undefined
    }
  },
  computed: {
    canCreate () {
      return this.$user.can(CREATE, this.$sdk.ratecard)
    },
    canUpdate () {
      return this.$user.can(UPDATE, this.$sdk.ratecard)
    },
    canDelete () {
      return this.$user.can(DELETE, this.$sdk.ratecard)
    }
  },
  beforeDestroy () {
    if (typeof this.notesDialogInstance !== 'undefined') {
      this.notesDialogInstance.$destroy()
      this.notesDialogInstance.$el.remove()
      this.notesDialogInstance = undefined
    }
  },
  methods: {
    sortCarrier (a, b) {
      return a.carrier.name.localeCompare(b.carrier.name)
    },
    async loadTable () {
      this.isLoading = true

      this.rateCards = await this
        .$sdk
        .ratecard
        .list(request => {
          request.page = this.page
          request.limit = this.perPage
          request.filters = this.filters
          request.include = ['profile', 'group', 'carrier', 'carrier.carrier_services', 'notes']
          request.order = this.sort.field ? `${this.sort.direction === 'desc' ? '-' : ''}${this.sort.field}` : null
        })

      this.total = this.rateCards.total

      this.isLoading = false
    },
    editRateCard (index, row) {
      this.rateCard = row.clone()
      this.isShowEditForm = true
    },
    duplicateRateCard (index, row) {
      this.rateCard = row.clone()
      this.isShowDuplicateForm = true
    },
    history (index, row) {
      // const existedGroupFilterIndex = this.filters.findIndex(f => f.field === 'group')
      // if (existedGroupFilterIndex !== -1) {
      //   this.filters[existedGroupFilterIndex].value = row.group.id
      // } else {
      //   this.filters.push(new Filter('group', IS, row.group.id))
      // }

      this.filters = []
      this.filters.push(new Filter('group', IS, row.group.id))

      this.loadTable()
    },
    exportCSV (index, row) {
      this.exportCSVRateCard = row
      this.isShowExportCSV = true
    },
    exportPDF (index, row) {
      this.exportPDFRateCard = row
      this.isShowExportPDF = true
    },
    notes (index, row) {
      if (typeof this.notesDialogInstance !== 'undefined') {
        this.notesDialogInstance.$destroy()
        this.notesDialogInstance.$el.remove()
        this.notesDialogInstance = undefined
      }

      const RateCardNotesDialogClass = Vue.extend(RateCardNotesDialog)

      this.notesDialogInstance = new RateCardNotesDialogClass({
        propsData: {
          rateCardId: row.id
        }
      })

      this.notesDialogInstance.$on('destroy', () => {
        this.notesDialogInstance.$destroy()
        this.notesDialogInstance.$el.remove()
        this.notesDialogInstance = undefined
      })

      this.notesDialogInstance.$mount()
      this.$root.$el.appendChild(this.notesDialogInstance.$el)
    },
    deleteRateCard (index, row) {
      this.$confirm(`Are you sure you want to delete the <b>${row.name}</b> Rate Card?`, 'Notice', {
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        dangerouslyUseHTMLString: true,
        showClose: false,
        type: 'warning'
      }).then(() => {
        this.isLoading = true
        row.delete()
          .then(() => this.$message({ message: 'Successfully deleted', type: 'success' }))
          .finally(() => this.loadTable())
      }).catch(() => {})
    },
    bulkDelete () {
      this.$confirm(`Are you sure you want to delete the following Rate Card${this.multiSelected.length > 1 ? 's' : ''}?<br><ul>${this.multiSelected.map(r => `<li>${r.name}</li>`).join('')}</ul>`, 'Notice', {
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        dangerouslyUseHTMLString: true,
        showClose: false,
        type: 'warning'
      }).then(async () => {
        this.isLoading = true

        try {
          await Promise.all(this.multiSelected.map(selected => selected.delete()))
          this.$message.success(`Successfully deleted ${this.multiSelected.length} Rate Card${this.multiSelected.length > 1 ? 's' : ''}`)
        } catch {
          this.$message.error('An error occurred while deleting')
        }

        this.isLoading = false

        this.loadTable()
      }).catch(() => {})
    },
    linkedAccounts (index, row) {
      this.rateCard = row
      this.isShowLinkedAccountsForm = true
    },
    handleSelectionChange (selected) {
      this.multiSelected = selected
    }
  }
}
</script>
